import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Globe, Clock, ChevronDown, ChevronUp, ImageOff } from 'lucide-react';
import PageWrapper from '../components/PageWrapper';
import Breadcrumbs from '../components/Breadcrumbs';
import MedicalText from '../components/MedicalText';
import { urologicInstrumentsContent } from '../data/urologicInstrumentsContent';

const UrologicInstruments = () => {
  const [language, setLanguage] = useState('en');
  const [expandedItems, setExpandedItems] = useState(new Set(['cystoscope']));

  const content = urologicInstrumentsContent;

  const toggleLanguage = () => {
    setLanguage(lang => lang === 'en' ? 'es' : 'en');
  };

  const toggleItem = (id) => {
    setExpandedItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  const expandAll = () => {
    setExpandedItems(new Set(content.instruments.map(i => i.id)));
  };

  const collapseAll = () => {
    setExpandedItems(new Set());
  };

  // Parse markdown-style formatting
  const renderDescription = (text) => {
    const lines = text.split('\n');
    const elements = [];
    let currentParagraph = [];

    const parseBoldText = (str) => {
      const parts = str.split(/(\*\*[^*]+\*\*)/g);
      return parts.map((part, idx) => {
        if (part.startsWith('**') && part.endsWith('**')) {
          return <strong key={idx}>{part.slice(2, -2)}</strong>;
        }
        return <MedicalText key={idx}>{part}</MedicalText>;
      });
    };

    lines.forEach((line, index) => {
      const trimmedLine = line.trim();
      
      if (trimmedLine === '') {
        if (currentParagraph.length > 0) {
          elements.push(
            <p key={`p-${index}`} className="instrument-paragraph">
              {parseBoldText(currentParagraph.join(' '))}
            </p>
          );
          currentParagraph = [];
        }
        return;
      }
      
      if (trimmedLine.startsWith('**') && trimmedLine.endsWith('**') && !trimmedLine.slice(2, -2).includes('**')) {
        if (currentParagraph.length > 0) {
          elements.push(
            <p key={`p-${index}`} className="instrument-paragraph">
              {parseBoldText(currentParagraph.join(' '))}
            </p>
          );
          currentParagraph = [];
        }
        const headerText = trimmedLine.slice(2, -2);
        elements.push(
          <h4 key={`h-${index}`} className="instrument-subheader">
            {headerText}
          </h4>
        );
        return;
      }
      
      if (trimmedLine.startsWith('•') || trimmedLine.startsWith('- ')) {
        if (currentParagraph.length > 0) {
          elements.push(
            <p key={`p-${index}`} className="instrument-paragraph">
              {parseBoldText(currentParagraph.join(' '))}
            </p>
          );
          currentParagraph = [];
        }
        const bulletText = trimmedLine.replace(/^[•-]\s*/, '');
        elements.push(
          <div key={`li-${index}`} className="instrument-list-item">
            <span className="list-bullet">•</span>
            <span>{parseBoldText(bulletText)}</span>
          </div>
        );
        return;
      }
      
      currentParagraph.push(trimmedLine);
    });

    if (currentParagraph.length > 0) {
      elements.push(
        <p key="p-final" className="instrument-paragraph">
          {parseBoldText(currentParagraph.join(' '))}
        </p>
      );
    }

    return elements;
  };

  // Placeholder component for missing images
  const ImagePlaceholder = ({ name }) => (
    <div className="instrument-image-placeholder" data-testid="image-placeholder">
      <ImageOff size={48} strokeWidth={1.5} />
      <p>{language === 'en' ? 'Image Pending' : 'Imagen Pendiente'}</p>
      <span>{language === 'en' ? 'Verified photo required' : 'Se requiere foto verificada'}</span>
    </div>
  );

  return (
    <PageWrapper>
      <Breadcrumbs items={[
        { label: 'Education Library', path: '/education' },
        { label: 'Urologic Instruments', path: '/education/urologic-instruments' }
      ]} />

      <article className="instruments-page" data-testid="instruments-page">
        {/* Hero Section */}
        <header className="instruments-header">
          <div className="instruments-hero-image">
            <img src={content.heroImage} alt="Urologic medical instruments" />
            <div className="hero-overlay" />
          </div>
          <div className="instruments-hero-content">
            <h1>{content.title[language]}</h1>
            <p className="instruments-subtitle">{content.subtitle[language]}</p>
            <div className="instruments-meta">
              <span><Clock size={16} /> 8 min read</span>
              <button 
                onClick={toggleLanguage}
                className="language-btn"
                data-testid="language-toggle"
              >
                <Globe size={16} />
                {language === 'en' ? 'Ver en Español' : 'View in English'}
              </button>
            </div>
          </div>
        </header>

        {/* Source Attribution for Hero */}
        {content.heroImageSource && (
          <div className="hero-source-attribution">
            <small>{language === 'en' ? 'Hero image source:' : 'Fuente de imagen:'} {content.heroImageSource}</small>
          </div>
        )}

        {/* Introduction */}
        <div className="instruments-intro">
          <p>
            {language === 'en' 
              ? 'During your urology care, you may encounter various medical instruments and devices. Understanding what these tools are and how they\'re used can help reduce anxiety and prepare you for procedures. This guide covers the most common urologic instruments you may see or hear about.'
              : 'Durante su atención urológica, puede encontrar varios instrumentos y dispositivos médicos. Comprender qué son estas herramientas y cómo se usan puede ayudar a reducir la ansiedad y prepararlo para los procedimientos. Esta guía cubre los instrumentos urológicos más comunes que puede ver o escuchar.'}
          </p>
          <div className="image-disclaimer">
            <small>
              {language === 'en'
                ? '📷 All images in this guide are sourced from verified medical repositories (Wikimedia Commons, NIH) with proper attribution.'
                : '📷 Todas las imágenes en esta guía provienen de repositorios médicos verificados (Wikimedia Commons, NIH) con atribución adecuada.'}
            </small>
          </div>
        </div>

        {/* Expand/Collapse Controls */}
        <div className="instruments-controls">
          <button onClick={expandAll} className="expand-btn">
            {language === 'en' ? 'Expand All' : 'Expandir Todo'}
          </button>
          <button onClick={collapseAll} className="collapse-btn">
            {language === 'en' ? 'Collapse All' : 'Colapsar Todo'}
          </button>
        </div>

        {/* Instruments List */}
        <div className="instruments-list">
          {content.instruments.map((instrument) => (
            <div 
              key={instrument.id} 
              className={`instrument-card ${expandedItems.has(instrument.id) ? 'expanded' : ''}`}
              data-testid={`instrument-${instrument.id}`}
            >
              <button 
                className="instrument-header"
                onClick={() => toggleItem(instrument.id)}
                aria-expanded={expandedItems.has(instrument.id)}
              >
                <div className="instrument-header-content">
                  <div className="instrument-thumb">
                    {instrument.image ? (
                      <img src={instrument.image} alt={instrument.name[language]} />
                    ) : (
                      <div className="thumb-placeholder">
                        <ImageOff size={20} />
                      </div>
                    )}
                  </div>
                  <h3>{instrument.name[language]}</h3>
                </div>
                {expandedItems.has(instrument.id) ? (
                  <ChevronUp size={24} />
                ) : (
                  <ChevronDown size={24} />
                )}
              </button>
              
              {expandedItems.has(instrument.id) && (
                <div className="instrument-content">
                  <div className="instrument-image-full">
                    {instrument.image ? (
                      <>
                        <img src={instrument.image} alt={instrument.name[language]} />
                        {instrument.imageSource && (
                          <p className="image-source-caption">
                            {instrument.imageSource[language]}
                          </p>
                        )}
                      </>
                    ) : (
                      <ImagePlaceholder name={instrument.name[language]} />
                    )}
                  </div>
                  <div className="instrument-description">
                    {renderDescription(instrument.description[language])}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Disclaimer */}
        <div className="instruments-disclaimer">
          <h4>{language === 'en' ? 'A Note About Your Care' : 'Una Nota Sobre Su Atención'}</h4>
          <p>
            {language === 'en'
              ? 'Every patient and procedure is different. Your urologist will explain which instruments will be used in your specific case and answer any questions you have. Don\'t hesitate to ask about anything you\'re unsure about.'
              : 'Cada paciente y procedimiento es diferente. Su urólogo le explicará qué instrumentos se utilizarán en su caso específico y responderá cualquier pregunta que tenga. No dude en preguntar sobre cualquier cosa de la que no esté seguro.'}
          </p>
        </div>

        {/* Related Topics */}
        <div className="related-section">
          <h3>{language === 'en' ? 'Related Topics' : 'Temas Relacionados'}</h3>
          <div className="related-chips">
            <Link to="/after-visit/cystoscopy" className="related-chip">Cystoscopy Aftercare</Link>
            <Link to="/education/catheter-flushing" className="related-chip">Catheter Care</Link>
            <Link to="/education/nephrostomy-tube-care" className="related-chip">Nephrostomy Tube Care</Link>
            <Link to="/common-concerns/bph-surgical-treatments" className="related-chip">BPH Surgical Options</Link>
          </div>
        </div>
      </article>
    </PageWrapper>
  );
};

export default UrologicInstruments;
